import React, { RefObject, useCallback, useRef, useState } from 'react';
import { PanelDockSide, readPanelPlacement, writePanelPlacement } from './panelDockStore.ts';
import { clampFloatRect, floatRectFor, PanelRect, snapSideFor } from './panelGeometry.ts';

/** How far the pointer must travel before a press on the header becomes a drag. */
const FLOAT_DRAG_THRESHOLD = 4;
/** Larger for a docked panel, so a slightly shaky click on its header never tears it off the edge. */
const UNDOCK_DRAG_THRESHOLD = 24;
/** Keeps the pointer on the header when a wide docked panel shrinks to the floating width under it. */
const GRAB_EDGE_MARGIN = 48;

// Presses on these start their own action, never a drag.
const INTERACTIVE = 'button, a, input, textarea, select, [role="button"]';

interface Options {
  key: string;
  enabled: boolean;
  floating: boolean;
  /** The floating window's element — moved directly while dragging, so a drag does not re-render the panel. */
  floatRef: RefObject<HTMLElement>;
}

/**
 * The header drag for dockable panels: moves a floating window, and pulls a docked one off its edge.
 *
 * One gesture covers both, which is why this is not PrimeReact Dialog's own drag: that one only starts
 * on a press on the Dialog's header and binds its move listeners after the enter transition, so a drag
 * that begins on the docked panel could not carry on into the window. Here the listeners live on the
 * window for the whole press — the container swaps underneath and the pointer keeps the window.
 *
 * - Docked: past UNDOCK_DRAG_THRESHOLD the panel is stored as floating at its floating size, positioned
 *   so the pointer still holds the header — the height shrinks on undock rather than a full-height
 *   column being dragged around.
 * - Floating: the window follows the pointer, kept on screen. Only the release is persisted.
 * - Pushed against an edge and a little past it (the window stays on screen, the pointer keeps going):
 *   released there, it docks to that edge. `snapPreview` shows which one while the drag is on.
 */
const usePanelDrag = ({ key, enabled, floating, floatRef }: Options) => {
  const [snapPreview, setSnapPreview] = useState<PanelDockSide | null>(null);
  // The latest mode, read from inside the gesture's listeners rather than captured at press time.
  const floatingRef = useRef(floating);
  floatingRef.current = floating;

  const onPointerDown = useCallback(
    (e: React.PointerEvent<HTMLElement>) => {
      if (!enabled || e.button !== 0 || (e.target as HTMLElement).closest(INTERACTIVE)) return;
      // The panel box the header belongs to — the docked Sidebar or the floating window.
      const panel = (e.currentTarget as HTMLElement).closest<HTMLElement>('.p-sidebar, [data-panel-float]');
      if (!panel) return;

      const start = { x: e.clientX, y: e.clientY };
      const box = panel.getBoundingClientRect();
      let grab = { x: start.x - box.left, y: start.y - box.top };
      let rect: PanelRect = { x: box.left, y: box.top, w: box.width, h: box.height };
      let dragging = false;
      let snapSide: PanelDockSide | null = null;
      const threshold = floatingRef.current ? FLOAT_DRAG_THRESHOLD : UNDOCK_DRAG_THRESHOLD;

      const onMove = (ev: PointerEvent) => {
        if (!dragging) {
          if (Math.hypot(ev.clientX - start.x, ev.clientY - start.y) < threshold) return;
          dragging = true;
          // PrimeReact's own class for this: no text selection while something is being dragged.
          document.body.classList.add('p-unselectable-text');

          if (!floatingRef.current) {
            const size = floatRectFor(readPanelPlacement(key));
            grab = { x: Math.min(grab.x, size.w - GRAB_EDGE_MARGIN), y: grab.y };
            rect = clampFloatRect({ ...size, x: ev.clientX - grab.x, y: ev.clientY - grab.y });
            writePanelPlacement(key, { ...readPanelPlacement(key), mode: 'float', ...rect });
          }
        }

        const unclamped = { ...rect, x: ev.clientX - grab.x, y: ev.clientY - grab.y };
        rect = clampFloatRect(unclamped);
        const el = floatRef.current;
        if (el) {
          el.style.left = `${rect.x}px`;
          el.style.top = `${rect.y}px`;
        }
        snapSide = snapSideFor(unclamped, ev.clientX);
        setSnapPreview(snapSide);
      };

      const onUp = (ev: PointerEvent) => {
        window.removeEventListener('pointermove', onMove);
        window.removeEventListener('pointerup', onUp);
        window.removeEventListener('pointercancel', onUp);
        if (!dragging) return;
        document.body.classList.remove('p-unselectable-text');
        setSnapPreview(null);

        const current = readPanelPlacement(key);
        const side = ev.type === 'pointerup' ? snapSide : null;
        // Snapping leaves the stored floating spot alone — the drop point is at the screen edge, which is
        // not where anyone wants the window when they float it again.
        if (side) writePanelPlacement(key, { ...current, mode: 'dock', side });
        else writePanelPlacement(key, { ...current, mode: 'float', x: rect.x, y: rect.y });
      };

      window.addEventListener('pointermove', onMove);
      window.addEventListener('pointerup', onUp);
      window.addEventListener('pointercancel', onUp);
    },
    [key, enabled, floatRef],
  );

  return { snapPreview, onPointerDown };
};

export default usePanelDrag;
